import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { ChangeDetectionStrategy, Component, computed, inject, linkedSignal, ViewContainerRef } from '@angular/core';
import { rxResource, takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ApiService, AssistResourceBM, resources as resourcesRequest } from '@cue/api';
import { LocalizedStringPipe } from '@cue/utilities';
import {
  cueArrowDown,
  cueCalendarCheck,
  cueClose,
  cueInfo,
  cueNavigationCircle,
  cueOrders,
  cueSpeaker,
  cueTablet,
  cueUser,
  IconComponent,
} from '@cue/ui/icons';
import { TranslocoPipe } from '@jsverse/transloco';
import { linkedQueryParam } from 'ngxtension/linked-query-param';
import { injectQueryParams } from 'ngxtension/inject-query-params';
import { catchError, forkJoin, map, of, startWith } from 'rxjs';

import { AuthService, LocaleService } from '@assist/shared/data';
import {
  DropdownlistComponent,
  DropdownsItemTemplateDirective,
  ValueTemplateDirective,
} from '@cue/assist/ui/dropdowns';
import { LoadingComponent } from '@cue/loading';
import { NavigationDetailInfo, NavigationService } from '@cue/navigation-services';
import { PoisCanBeShownInResourcePipe } from '@cue/navigation-widgets';
import { translation } from '@cue/translations';

import {
  MultiViewRenewControl,
  MultiviewRenewControlComponent,
} from '../multiview-renew-control/multiview-renew-control.component';
import { MultiviewRenewDeviceComponent } from '../multiview-renew-device/multiview-renew-device.component';
import { MultiviewRenewEventsComponent } from '../multiview-renew-events/multiview-renew-events.component';
import { MultiviewRenewMoreComponent } from '../multiview-renew-more/multiview-renew-more.component';
import { MultiviewRenewNavigationComponent } from '../multiview-renew-navigation/multiview-renew-navigation.component';
import { MultiviewRenewRequestsComponent } from '../multiview-renew-requests/multiview-renew-requests.component';
import { MultiviewRenewDetailQrComponent } from '../multiview-renew-detail-qr/multiview-renew-detail-qr.component';
import { MultiviewRenewDetailResourceComponent } from '../multiview-renew-detail-resource/multiview-renew-detail-resource.component';

type BarItemType = 'detail' | 'navigation' | 'events' | 'requests' | 'control' | 'device' | 'more';
type MultiviewSource = 'resource' | 'qr';
export type MultiViewRenewData = {
  mode: 'anonymous' | 'authenticated';
};

type NavigationDetailParams = {
  resourceId?: number;
  qrCodeId?: string;
  anonymous: boolean;
};

type ServiceAvailability = Record<
  string,
  {
    id: string;
    enableForAssist: boolean;
    resourceRequestAllowed: boolean;
    eventRequestAllowed: boolean;
  }
>;

@Component({
  selector: 'assist-multiview-renew',
  imports: [
    IconComponent,
    LoadingComponent,
    MultiviewRenewControlComponent,
    MultiviewRenewDeviceComponent,
    MultiviewRenewEventsComponent,
    MultiviewRenewMoreComponent,
    MultiviewRenewNavigationComponent,
    MultiviewRenewRequestsComponent,
    MultiviewRenewDetailQrComponent,
    MultiviewRenewDetailResourceComponent,
    TranslocoPipe,
    DropdownlistComponent,
    DropdownsItemTemplateDirective,
    ValueTemplateDirective,
    ReactiveFormsModule,
  ],
  templateUrl: './multiview-renew.component.html',
  styleUrl: './multiview-renew.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [LocalizedStringPipe, PoisCanBeShownInResourcePipe],
})
export class MultiviewRenewComponent {
  protected readonly ICONS = {
    cueArrowDown,
    cueInfo,
    cueNavigationCircle,
    cueCalendarCheck,
    cueOrders,
    cueTablet,
    cueSpeaker,
    cueClose,
    cueUser,
  };
  protected readonly translation = translation;

  protected readonly dialogData = inject(DIALOG_DATA) as MultiViewRenewData;
  protected readonly dialogRef = inject(DialogRef);
  protected readonly localeService = inject(LocaleService);
  protected readonly notificationContainer = inject(ViewContainerRef);

  private readonly apiService = inject(ApiService);
  private readonly authService = inject(AuthService);
  private readonly localizedStringPipe = inject(LocalizedStringPipe);
  private readonly navigationService = inject(NavigationService);
  private readonly poisCanBeShownInResourcePipe = inject(PoisCanBeShownInResourcePipe);

  protected readonly multiviewQr = injectQueryParams('multiviewQr');
  protected readonly multiviewResource = injectQueryParams('multiviewResource');
  private readonly multiviewQrScannedAt = injectQueryParams('multiviewQrScannedAt');
  protected readonly currentLanguageControl = new FormControl<{ text: string; flag: string } | null>(null);

  protected readonly source = computed<MultiviewSource | null>(() => {
    if (this.multiviewResource()) {
      return 'resource';
    }
    if (this.multiviewQr()) {
      return 'qr';
    }
    return null;
  });

  protected readonly isAnonymous = toSignal(
    this.authService.apiTokenChanged.pipe(
      map(() => this.authService.getApiToken() == null),
      startWith(this.dialogData.mode === 'anonymous')
    ),
    { requireSync: true }
  );
  protected readonly isResourceSource = computed(() => this.source() === 'resource');
  protected readonly isQrSource = computed(() => this.source() === 'qr');
  protected readonly qrScanTimestamp = computed(() => {
    if (!this.isQrSource()) {
      return null;
    }

    const timestamp = Number(this.multiviewQrScannedAt());
    const now = Date.now();
    return Number.isFinite(timestamp) && timestamp <= now && timestamp >= now - 5 * 60 * 1000 ? timestamp : null;
  });

  protected readonly resources = rxResource({
    params: () => (!this.isAnonymous() ? (this.source() ?? undefined) : undefined),
    stream: () => this.apiService.call(resourcesRequest('/api/assist/resources')),
  });

  private readonly qrSourceDetail = rxResource({
    params: (): NavigationDetailParams | undefined => {
      const qrCodeId = this.multiviewQr();
      return this.isQrSource() && qrCodeId ? { qrCodeId, anonymous: this.isAnonymous() } : undefined;
    },
    stream: ({ params }) =>
      this.navigationService
        .getNavigationDetailForMultiview({
          qrCodeId: params.qrCodeId,
          kioskId: null,
          appId: null,
        })
        .pipe(
          map((detail) => detail ?? null),
          catchError(() => of(null))
        ),
  });

  protected readonly qrSourceDetailValue = computed(() =>
    this.qrSourceDetail.hasValue() ? this.qrSourceDetail.value() : null
  );

  protected readonly resource = computed<AssistResourceBM | null>(() => {
    if (this.isAnonymous() || !this.resources.hasValue()) {
      return null;
    }

    const qrDetail = this.qrSourceDetailValue();
    const reference = (
      this.isResourceSource()
        ? this.multiviewResource()
        : qrDetail?.resourceGuid || (qrDetail?.resourceId != null ? String(qrDetail.resourceId) : null)
    )
      ?.trim()
      .toLocaleLowerCase();
    if (!reference) {
      return null;
    }

    return (
      this.resources
        .value()
        .find(
          (resource) => resource.id?.toLocaleLowerCase() === reference || String(resource.idForFiltering) === reference
        ) ?? null
    );
  });

  protected readonly resourceId = computed<number | null>(() => {
    const resourceId = this.resource()?.idForFiltering;
    if (resourceId != null) {
      return resourceId;
    }

    if (this.isResourceSource()) {
      const numericResourceId = Number(this.multiviewResource());
      return Number.isInteger(numericResourceId) && numericResourceId > 0 ? numericResourceId : null;
    }

    return this.qrSourceDetailValue()?.resourceId ?? null;
  });

  private readonly detailParams = computed<NavigationDetailParams | undefined>(() => {
    const resourceId = this.resourceId();
    return resourceId == null ? undefined : { resourceId, anonymous: this.isAnonymous() };
  });

  protected readonly serverDetail = rxResource({
    params: () => this.detailParams(),
    stream: ({ params }) =>
      this.navigationService
        .getNavigationDetailForMultiview({
          resourceId: params.resourceId,
          kioskId: null,
          appId: null,
        })
        .pipe(
          map((detail) => detail ?? null),
          catchError(() => of(null))
        ),
  });

  protected readonly serverDetailValue = computed(() => {
    if (this.serverDetail.hasValue()) {
      return this.serverDetail.value();
    }
    return this.qrSourceDetailValue();
  });

  private readonly resourceQrCodeIds = computed(() => {
    const serverDetail = this.serverDetailValue();
    if (this.isAnonymous() || this.resourceId() == null || !serverDetail) {
      return [];
    }

    const visibleQrCodes = this.poisCanBeShownInResourcePipe.transform(serverDetail.qrCodes ?? []);
    return [...new Set(visibleQrCodes.map((qrCode) => qrCode.qrCodeId).filter((id): id is string => !!id))];
  });

  private readonly resourceQrDetails = rxResource({
    params: () => {
      const qrCodeIds = this.resourceQrCodeIds();
      return qrCodeIds.length > 0 ? qrCodeIds : undefined;
    },
    stream: ({ params }) =>
      forkJoin(
        params.map((qrCodeId) =>
          this.navigationService
            .getNavigationDetailForMultiview({
              qrCodeId,
              kioskId: null,
              appId: null,
            })
            .pipe(
              map((detail) => detail ?? null),
              catchError(() => of(null))
            )
        )
      ).pipe(map((details) => details.filter((detail): detail is NavigationDetailInfo => detail != null))),
  });

  private readonly qrDetails = computed<NavigationDetailInfo[]>(() => {
    if (!this.isAnonymous() && this.resourceId() != null) {
      return this.resourceQrDetails.hasValue() ? this.resourceQrDetails.value() : [];
    }

    const detail = this.qrSourceDetailValue() ?? this.serverDetailValue();
    return detail ? [detail] : [];
  });

  protected readonly devices = computed(() => this.qrDetails().filter((detail) => detail.unitInformation != null));
  protected readonly unitInfos = computed(() => this.devices().map((detail) => detail.unitInformation));

  protected readonly controls = computed<MultiViewRenewControl[]>(() => {
    const qrCodeList =
      !this.isAnonymous() && this.resourceId() != null
        ? this.resourceQrCodeIds()
        : this.multiviewQr()
          ? [this.multiviewQr()]
          : [];

    return this.qrDetails()
      .filter((detail) => detail.qrCodeId != null && detail.objects?.length > 0)
      .map((detail) => ({
        poi: detail,
        objects: detail.objects,
        qrCodeId: detail.qrCodeId,
        qrCodeListOfResource: qrCodeList,
      }));
  });

  protected readonly hasServicesAvailable = rxResource({
    params: () => (!this.isAnonymous() ? this.resource()?.id : undefined),
    stream: ({ params }) =>
      this.apiService
        .call<unknown, ServiceAvailability>({
          url: '/api/assist/resources/services/availability',
          method: 'post',
          payload: { guids: [params] },
        })
        .pipe(
          map((availability) => {
            const resourceAvailability = availability[params];
            return Boolean(resourceAvailability?.enableForAssist && resourceAvailability.resourceRequestAllowed);
          })
        ),
  });

  protected readonly hasLocation = computed(() => {
    if (this.isQrSource()) {
      const detail = this.qrSourceDetailValue();
      return Boolean(detail && !detail.notNavigationPoint && detail.areaId != null);
    }

    if (this.isResourceSource()) {
      const resource = this.resource();
      if (resource) {
        return Boolean(resource.navigationEnabled && resource.mapInfos?.length > 0);
      }

      const detail = this.serverDetailValue();
      return Boolean(detail && !detail.notNavigationPoint && detail.areaId != null);
    }

    const detail = this.serverDetailValue();
    return Boolean(detail && !detail.notNavigationPoint && detail.areaId != null);
  });

  private readonly sourceAvailable = computed(() =>
    this.isResourceSource()
      ? this.resource() != null || this.serverDetailValue() != null
      : this.isQrSource() && this.qrSourceDetailValue() != null
  );

  protected readonly availableBarItems = computed<BarItemType[]>(() => {
    const items: BarItemType[] = [];

    if (this.sourceAvailable()) {
      items.push('detail');
    }

    if (!this.isAnonymous() && this.resource()) {
      items.push('events');
    }

    if (
      !this.isAnonymous() &&
      this.resource() &&
      this.hasServicesAvailable.hasValue() &&
      this.hasServicesAvailable.value()
    ) {
      items.push('requests');
    }

    if (this.controls().length > 0) {
      items.push('control');
    }

    if (this.devices().length > 0) {
      items.push('device');
    }

    if (this.hasLocation()) {
      items.push('navigation');
    }

    if (this.isAnonymous()) {
      items.push('more');
    }

    return items;
  });

  protected readonly queryParamTab = linkedQueryParam('tab', {
    queryParamsHandling: 'merge',
    replaceUrl: true,
  });

  protected readonly activeBarItem = linkedSignal<BarItemType | null>(() => {
    const items = this.availableBarItems();
    const preferredItem = this.queryParamTab();
    if (preferredItem && items.includes(preferredItem as BarItemType)) {
      return preferredItem as BarItemType;
    }
    return items[0] ?? null;
  });

  protected readonly loading = computed(() => {
    if (this.isResourceSource()) {
      return (
        this.resources.isLoading() ||
        (this.resourceId() != null && this.serverDetail.isLoading()) ||
        this.hasServicesAvailable.isLoading()
      );
    }
    return (
      this.isQrSource() &&
      (this.qrSourceDetail.isLoading() ||
        (!this.isAnonymous() &&
          (this.resources.isLoading() ||
            (this.resourceId() != null && this.serverDetail.isLoading()) ||
            this.hasServicesAvailable.isLoading())))
    );
  });

  protected readonly title = computed(() => {
    const resource = this.isResourceSource() ? this.resource() : null;
    if (resource) {
      return (
        this.localizedStringPipe.transform(resource.displayNameForApp) ||
        this.localizedStringPipe.transform(resource.displayName) ||
        resource.name
      );
    }

    const detail = this.isQrSource() ? this.qrSourceDetailValue() : this.serverDetailValue();
    return detail ? this.localizedStringPipe.transform(detail.name) : 'POI';
  });

  protected readonly areaId = computed(() => {
    if (this.isQrSource()) {
      return this.qrSourceDetailValue()?.areaId ?? null;
    }
    if (this.isResourceSource()) {
      return this.resource()?.mapInfos?.[0]?.areaId ?? this.serverDetailValue()?.areaId ?? null;
    }
    return this.serverDetailValue()?.areaId ?? null;
  });

  constructor() {
    this.currentLanguageControl.setValue(this.localeService.currentLanguage, { emitEvent: false });
    this.currentLanguageControl.valueChanges.pipe(takeUntilDestroyed()).subscribe((language) => {
      if (language) {
        this.localeService.currentLanguage = language;
      }
    });
  }

  protected hasBarItem(item: BarItemType): boolean {
    return this.availableBarItems().includes(item);
  }

  protected setActiveBarItem(item: BarItemType): void {
    this.queryParamTab.set(item);
  }
}
